import { Language } from "./language";

/**
 * Recall status for quiz answers - matches FSRS rating names
 */
export enum RecallStatus {
	/** Forgot - FSRS Rating.Again (1) */
	Again = 1,
	/** Hard to recall - FSRS Rating.Hard (2) */
	Hard = 2,
	/** Recalled correctly - FSRS Rating.Good (3) */
	Good = 3,
	/** Perfect recall - FSRS Rating.Easy (4) */
	Easy = 4
}

export enum WordStatus {
	New = 'New',
	Weak = 'Weak',
	Moderate = 'Moderate',
	Strong = 'Strong',
	Perfect = 'Perfect'
}

export interface SaveUserWordRequest {
	userId: string;
	translatedWordId: string;
}

export interface SaveUserWordResponse {
	success: boolean;
	translatedWordId: string;
	/** When success is false, an optional reason code. */
	reason?: 'bookmark_limit_reached';
}

export const FREE_BOOKMARK_LIMIT = 30;
// export const FREE_BOOKMARK_LIMIT = 30;

export interface GetUserWordsRequest {
	userId: string;
	nativeLanguage?: Language;
	learnedLanguage?: Language;
	translatedWordIds?: string[];
	batchKey?: string;
	limit?: number;
}

export interface UserWordDto {
	id: string;
	translatedWordId: string;
	word: string;
	translation: string;
	language: Language;
	transliteration?: string;
	createdAt: string;
	lastSeenAt: string;
	status: WordStatus;        // Calculated word learning status
	// FSRS-specific fields
	fsrsState?: number;        // Card state: 0=New, 1=Learning, 2=Review, 3=Relearning
	stability?: number;        // Memory stability in days
	difficulty?: number;       // Difficulty (0-10)
	retrievability?: number;   // Current probability of recall (0-1)
}

export interface GetUserWordsResponse {
	words: UserWordDto[];
	nextBatchKey?: string;
}

export interface GetWordsToRepeatRequest {
	userId: string;
	nativeLanguage?: Language;
	learnedLanguage?: Language;
	currentDate?: string; // ISO date string for test mode
	nextBatchKey?: string;
}

export interface WordToRepeatDto extends UserWordDto {
	nextReviewDate?: string;
	successRate: number;
	totalAttempts: number;
	// FSRS-specific fields
	fsrsState?: number;        // Card state: 0=New, 1=Learning, 2=Review, 3=Relearning
	stability?: number;        // Memory stability in days
	difficulty?: number;       // Difficulty (0-10)
	retrievability?: number;   // Current probability of recall (0-1)
}

export interface GetWordsToRepeatResponse {
	words: WordToRepeatDto[];
	nextBatchKey?: string;
}

export interface RecordQuizAttemptRequest {
	userId: string;
	translatedWordId: string;
	recallStatus: RecallStatus;
	isCorrect?: boolean;
	userTimeZoneOffset: number;
	currentDate?: string; // ISO date string for test mode
}

export interface RecordQuizAttemptResponse {
	success: boolean;
	nextReviewDate?: string;
}

export interface GetWordStatsRequest {
	userId: string;
	nativeLanguage?: Language;
	learnedLanguage?: Language;
}

export interface GetWordStatsResponse {
	totalWords: number;
	wordsToRepeat: number;
}

export interface GetDailyWordStatsRequest {
	userId: string;
	userTimeZoneOffset: number;
	nativeLanguage?: Language;
	learnedLanguage?: Language;
}

export interface UserDailyWordsInfo {
	/**
	 * Words bookmarked today (legacy key kept as requested).
	 */
	learned: string[];
	/**
	 * Alias for `learned`, kept for UI clarity.
	 */
	bookmarked: string[];
	/**
	 * Words validated today (recall status Good/Easy today).
	 */
	validated: string[];
	/**
	 * How many words are still needed to hit daily target.
	 */
	needToLearn: number;
}

export interface GetLast30DaysStatsRequest {
	userId: string;
	sourceLang?: Language;
	targetLang?: Language;
	userTimeZoneOffset?: number;
}

export interface UserWordDayStat {
	date: string;
	count: number;
}

export interface GetSavedWordsRequest {
	userId: string;
	nativeLanguage?: Language;
	learnedLanguage?: Language;
	nextBatchKey?: string;
}

export interface GetSavedWordsResponse {
	words: UserWordDto[];
	nextBatchKey?: string;
}

export interface RemoveUserWordRequest {
	userId: string;
	translatedWordId: string;
}

export interface CheckUserWordsRequest {
	userId: string;
	translatedWordIds: string[];
}

export interface CheckUserWordsResponse {
	savedWordIds: string[];
}

/**
 * Quiz options request
 */
export type QuizMode = 'to-repeat' | 'user-words' | 'article' | 'daily';

export interface GetQuizOptionsRequest {
	userId?: string; // Required if mode is 'to-repeat' or 'user-words'
	nativeLanguage: Language;
	learnedLanguage: Language;
	mode: QuizMode;
	articleTranslationId?: string; // Required if mode is 'article'
	wordIds?: string[]; // Required if mode is 'daily'
	maxSteps?: number; // Optional limit on number of quiz steps
}

/**
 * Translated word DTO for quiz options
 */
export interface TranslatedWordDto {
	id: string;
	sourceWord: string;
	translation: string;
	transliteration?: string;
	learnedLanguage: Language;
	nativeLanguage: Language;
}

/**
 * Single quiz step with correct word and invalid options
 */
export interface QuizStep {
	correctWord: TranslatedWordDto;
	invalidOptions: TranslatedWordDto[];
}

/**
 * Quiz options response
 */
export interface GetQuizOptionsResponse {
	steps: QuizStep[];
}
